import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutGrid, Store, User, LogOut, FolderKanban, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import logoNavbar from "../assets/brand/logo-navbar.png";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";

export const Navbar = () => {
  const { t } = useTranslation();
  const { user, profile, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (profile?.name || user?.email || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const dashboardPath = profile?.role === "designer" ? "/designer" : "/lab";

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to={user ? dashboardPath : "/"} className="flex items-center" data-testid="navbar-logo">
          <img
            src={logoNavbar}
            alt="DentalCAD Connect"
            className="h-8 w-auto sm:h-9"
            style={{ maxWidth: "70vw" }}
          />
        </Link>

        <nav className="flex items-center gap-2 sm:gap-3">
          {user && (
            <>
              {profile?.role !== "designer" && (
                <Link to={dashboardPath} className="hidden sm:block">
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="nav-dashboard">
                    <LayoutGrid className="h-4 w-4" />
                    {t("nav.dashboard")}
                  </Button>
                </Link>
              )}
              {profile?.role === "designer" && (
                <>
                  <Link to="/designer" className="hidden sm:block">
                    <Button variant="ghost" size="sm" className="gap-2" data-testid="nav-my-cases">
                      <FolderKanban className="h-4 w-4" />
                      {t("nav.myCases")}
                    </Button>
                  </Link>
                  <Link to="/marketplace" className="hidden sm:block">
                    <Button variant="ghost" size="sm" className="gap-2" data-testid="nav-marketplace">
                      <Store className="h-4 w-4" />
                      {t("nav.marketplace")}
                    </Button>
                  </Link>
                </>
              )}
            </>
          )}

          <LanguageSwitcher />

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="outline-none">
                  <Avatar className="h-9 w-9 border border-border">
                    <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{profile?.name || user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(dashboardPath)} data-testid="menu-dashboard">
                  <LayoutGrid className="mr-2 h-4 w-4" /> {t("nav.dashboard")}
                </DropdownMenuItem>
                {profile?.role === "designer" && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/marketplace")} data-testid="menu-marketplace">
                      <Store className="mr-2 h-4 w-4" /> {t("nav.marketplace")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/designer/profile")} data-testid="menu-profile">
                      <User className="mr-2 h-4 w-4" /> {t("nav.myProfile")}
                    </DropdownMenuItem>
                  </>
                )}
                {profile?.role === "lab" && (
                  <DropdownMenuItem onClick={() => navigate("/lab/account")} data-testid="menu-lab-profile">
                    <User className="mr-2 h-4 w-4" /> {t("nav.myProfile")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  data-testid="menu-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/why" className="hidden sm:block">
                <Button variant="ghost" size="sm" data-testid="nav-why">{t("nav.why")}</Button>
              </Link>
              <Link to="/auth">
                <Button size="sm" className="rounded-full bg-brand text-white hover:bg-brand/90" data-testid="nav-login">
                  {t("nav.login")}
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};
